import React, { useState, useEffect, useMemo } from 'react';
import { useParams, useNavigate, Link } from 'react-router-dom';
import { Card, CardContent } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Badge } from '../../components/ui/badge';
import { Loader2, ArrowLeft, Edit, Eye, Users, Clock, BarChart3, Star, ChevronRight, FileText, Download, Lock, Brain, MessageSquare } from 'lucide-react';
import { programsAPI, applicationsAPI, evaluatorsAPI, getImageUrl } from '../../services/api';
import { useLanguage } from '../../context/LanguageContext';
import { useAuth } from '../../context/AuthContext';
import { useToast } from '../../hooks/use-toast';
import { PaywallModal } from '../../components/PaywallModal';

const STAGE_COLORS = [
  { bg: 'bg-indigo-100', text: 'text-indigo-700', bar: 'bg-indigo-600', border: 'border-indigo-300' },
  { bg: 'bg-blue-100', text: 'text-blue-700', bar: 'bg-blue-500', border: 'border-blue-300' },
  { bg: 'bg-orange-100', text: 'text-orange-700', bar: 'bg-orange-500', border: 'border-orange-300' },
  { bg: 'bg-emerald-100', text: 'text-emerald-700', bar: 'bg-emerald-500', border: 'border-emerald-300' },
  { bg: 'bg-rose-100', text: 'text-rose-700', bar: 'bg-rose-500', border: 'border-rose-300' },
];

const VERTICAL_COLORS = ['#6366f1', '#10b981', '#f97316', '#a1a1aa', '#ec4899', '#8b5cf6', '#06b6d4'];

const STATUS_MAP = {
  submitted: { es: 'Recibidas', en: 'Received' },
  under_review: { es: 'En Revisión', en: 'Under Review' },
  scoring_in_progress: { es: 'Evaluación', en: 'Scoring' },
  finalist: { es: 'Preselección', en: 'Pre-selection' },
  accepted: { es: 'Ganadores', en: 'Winners' },
  rejected: { es: 'Rechazados', en: 'Rejected' },
};

export default function ChallengeDashboard() {
  const { id } = useParams();
  const navigate = useNavigate();
  const { language } = useLanguage();
  const { user } = useAuth();
  const { toast } = useToast();
  const [program, setProgram] = useState(null);
  const [applications, setApplications] = useState([]);
  const [scores, setScores] = useState({});
  const [loading, setLoading] = useState(true);
  const [exporting, setExporting] = useState(false);
  const [planInfo, setPlanInfo] = useState(null);
  const [paywallOpen, setPaywallOpen] = useState(false);
  const [paywallTrigger, setPaywallTrigger] = useState('applications');

  useEffect(() => {
    const fetchData = async () => {
      try {
        const userId = user?.id || user?._id;
        const [prog, apps, plan] = await Promise.all([
          programsAPI.getById(id),
          applicationsAPI.getByProgram(id, userId),
          userId ? programsAPI.getPlanInfo(userId).catch(() => null) : null
        ]);
        setProgram(prog);
        setApplications(apps);
        if (plan) setPlanInfo(plan);

        // Fetch scores for each application
        try {
          const scoresRes = await evaluatorsAPI.getScores({ programId: id });
          const scoresMap = {};
          scoresRes.forEach(s => {
            if (!scoresMap[s.applicationId]) scoresMap[s.applicationId] = [];
            scoresMap[s.applicationId].push(s);
          });
          setScores(scoresMap);
        } catch (e) {
          console.error('Error fetching scores:', e);
        }
      } catch (e) {
        console.error('Error loading challenge dashboard:', e);
      } finally {
        setLoading(false);
      }
    };
    fetchData();
  }, [id]);

  const stats = useMemo(() => {
    if (!program || !applications) return null;

    const total = applications.length;
    const deadline = program.deadline ? new Date(program.deadline) : null;
    const now = new Date();
    const daysLeft = deadline ? Math.max(0, Math.ceil((deadline - now) / (1000 * 60 * 60 * 24))) : null;

    // Status pipeline
    const statusCounts = {};
    applications.forEach(app => {
      const s = app.status || 'submitted';
      statusCounts[s] = (statusCounts[s] || 0) + 1;
    });

    // Verticals from program config
    const verticalCounts = {};
    const verticals = program.verticals || [];
    if (verticals.length > 0) {
      // Count applications per vertical from customFieldsData
      applications.forEach(app => {
        const data = app.customFieldsData || {};
        const v = data.vertical || data.categoria || data.category || 'Otro';
        const matched = verticals.find(vert => vert.toLowerCase() === v.toLowerCase()) || 'Otro';
        verticalCounts[matched] = (verticalCounts[matched] || 0) + 1;
      });
      // Ensure all defined verticals appear
      verticals.forEach(v => {
        if (!verticalCounts[v]) verticalCounts[v] = 0;
      });
    }

    return { total, daysLeft, deadline, statusCounts, verticalCounts };
  }, [program, applications]);

  const handleExportExcel = async () => {
    if (planInfo?.is_free) {
      setPaywallTrigger('export');
      setPaywallOpen(true);
      return;
    }
    try {
      setExporting(true);
      const blob = await applicationsAPI.exportExcel(id);
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `${program?.name?.replace(/\s+/g, '_') || 'postulaciones'}_postulaciones.xlsx`;
      document.body.appendChild(a);
      a.click();
      a.remove();
      window.URL.revokeObjectURL(url);
      toast({ title: language === 'es' ? 'Archivo descargado' : 'File downloaded' });
    } catch (e) {
      console.error('Export error:', e);
      toast({ title: language === 'es' ? 'Error al exportar' : 'Export error', variant: 'destructive' });
    } finally {
      setExporting(false);
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <Loader2 className="w-10 h-10 animate-spin text-purple-600" />
      </div>
    );
  }

  if (!program) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <p className="text-gray-500">{language === 'es' ? 'Desafío no encontrado' : 'Challenge not found'}</p>
      </div>
    );
  }

  const pipelineStatuses = ['submitted', 'under_review', 'scoring_in_progress', 'finalist', 'accepted'];
  const maxPipelineCount = Math.max(1, ...pipelineStatuses.map(s => stats.statusCounts[s] || 0));

  const verticalEntries = Object.entries(stats.verticalCounts).sort((a, b) => b[1] - a[1]);
  const verticalTotal = verticalEntries.reduce((sum, [, c]) => sum + c, 0) || stats.total;

  // Donut chart
  const donutR = 80;
  const donutStroke = 24;
  const donutC = 2 * Math.PI * donutR;
  let donutOffset = 0;

  const getAvgScore = (appId) => {
    const appScores = scores[appId];
    if (!appScores || appScores.length === 0) return null;
    const avg = appScores.reduce((sum, s) => sum + (s.weightedScore || s.totalScore || 0), 0) / appScores.length;
    return avg.toFixed(1);
  };

  return (
    <div className="min-h-screen bg-[#eef0f4]" data-testid="challenge-dashboard">
      {/* Hero Header with Image */}
      <div className="relative">
        <div className="h-48 w-full overflow-hidden bg-gradient-to-r from-indigo-900 to-purple-900">
          {program.image && (
            <img
              src={getImageUrl(program.image)}
              alt={program.name}
              className="w-full h-full object-cover opacity-40"
            />
          )}
        </div>
        <div className="absolute inset-0 flex items-end">
          <div className="max-w-[1600px] mx-auto px-4 sm:px-6 pb-5">
            <div className="flex flex-col sm:flex-row sm:items-end justify-between gap-3">
              <div>
                <Button variant="ghost" size="sm" onClick={() => navigate('/organization-dashboard')} className="text-white/80 hover:text-white hover:bg-white/10 mb-2" data-testid="back-to-org-btn">
                  <ArrowLeft className="w-4 h-4 mr-1" />
                  {language === 'es' ? 'Volver' : 'Back'}
                </Button>
                <h1 className="text-xl sm:text-3xl font-bold text-white">{program.name}</h1>
                <p className="text-white/70 mt-1">{program.organization} — {program.category}</p>
              </div>
              <div className="flex flex-wrap items-center gap-3">
                <Button variant="outline" size="sm" className="bg-white/10 border-white/30 text-white hover:bg-white/20" onClick={() => navigate(`/programs/${id}`)} data-testid="view-challenge-btn">
                  <Eye className="w-4 h-4 mr-1" />
                  {language === 'es' ? 'Ver Desafio' : 'View'}
                </Button>
                <Button size="sm" className="bg-white text-purple-900 hover:bg-white/90" onClick={() => navigate('/organization-dashboard')} data-testid="edit-challenge-btn">
                  <Edit className="w-4 h-4 mr-1" />
                  {language === 'es' ? 'Editar' : 'Edit'}
                </Button>
                <Button size="sm" className="bg-amber-400 text-gray-900 hover:bg-amber-300 font-semibold" onClick={() => navigate(`/organization/ai-evaluators/${id}`)} data-testid="ai-evaluators-btn">
                  <Brain className="w-4 h-4 mr-1" />
                  {language === 'es' ? 'Evaluar con IA' : 'AI Evaluate'}
                </Button>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Dashboard Content - Main + Sidebar */}
      <div className="max-w-[1600px] mx-auto px-4 sm:px-6 py-6 sm:py-8">
        <div className="flex flex-col lg:flex-row gap-6">
          {/* Main Content */}
          <div className="flex-1 min-w-0 space-y-6">

            {/* Big Numbers Row */}
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              {/* Total Applications */}
              <Card className="glass-card border-none" data-testid="total-applications-card">
                <CardContent className="p-6 sm:p-10">
                  <p className="text-xs font-semibold uppercase tracking-widest text-stone-400 mb-4">
                    {language === 'es' ? 'TOTAL POSTULACIONES' : 'TOTAL APPLICATIONS'}
                  </p>
                  <p className="text-[60px] sm:text-[90px] lg:text-[120px] font-black text-indigo-700 leading-none tracking-tight">{stats.total}</p>
                  <div className="w-full bg-stone-200 rounded-full h-2.5 mt-6 mb-3">
                    <div className="bg-indigo-600 h-2.5 rounded-full transition-all" style={{ width: `${Math.min(100, stats.total > 0 ? 100 : 0)}%` }} />
                  </div>
                  <div className="flex items-center gap-2 text-sm text-stone-500">
                    <Users className="w-4 h-4" />
                    <span>{stats.total} {language === 'es' ? 'postulaciones recibidas' : 'applications received'}</span>
                  </div>
                </CardContent>
              </Card>

              {/* Days to Close */}
              <Card className="glass-card border-none" data-testid="days-remaining-card">
                <CardContent className="p-6 sm:p-10">
                  <p className="text-xs font-semibold uppercase tracking-widest text-stone-400 mb-4">
                    {language === 'es' ? 'DÍAS PARA EL CIERRE' : 'DAYS TO CLOSE'}
                  </p>
                  {stats.daysLeft !== null ? (
                    <>
                      <p className={`text-[60px] sm:text-[90px] lg:text-[120px] font-black leading-none tracking-tight ${
                        stats.daysLeft <= 7 ? 'text-red-600' : stats.daysLeft <= 14 ? 'text-amber-600' : 'text-stone-800'
                      }`}>
                        {stats.daysLeft}
                      </p>
                      <div className="w-full bg-stone-200 rounded-full h-2.5 mt-6 mb-3">
                        <div className={`h-2.5 rounded-full transition-all ${stats.daysLeft <= 7 ? 'bg-red-500' : stats.daysLeft <= 14 ? 'bg-amber-500' : 'bg-stone-600'}`}
                          style={{ width: `${Math.max(5, Math.min(100, (stats.daysLeft / 90) * 100))}%` }}
                        />
                      </div>
                      <div className="flex items-center gap-2 text-sm text-stone-500">
                        <Clock className="w-4 h-4" />
                        <span>{language === 'es' ? 'cierra' : 'closes'} {stats.deadline.toLocaleDateString(language === 'es' ? 'es-CL' : 'en-US', { day: 'numeric', month: 'short', year: 'numeric' })}</span>
                      </div>
                    </>
                  ) : (
                    <p className="text-[60px] sm:text-[90px] lg:text-[120px] font-black text-stone-300 leading-none">—</p>
                  )}
                </CardContent>
              </Card>
            </div>

            {/* Verticals Pie Chart */}
            {verticalEntries.length > 0 && (
              <Card className="glass-card border-none" data-testid="verticals-card">
                <CardContent className="p-6 sm:p-10">
                  <p className="text-xs font-semibold uppercase tracking-widest text-stone-400 mb-8">
                    {language === 'es' ? 'VERTICALES' : 'VERTICALS'}
                  </p>
                  <div className="flex flex-col lg:flex-row items-center gap-12">
                    <div className="relative shrink-0">
                      <svg width="220" height="220" viewBox="0 0 220 220">
                        {verticalEntries.map(([name, count], i) => {
                          const pct = verticalTotal > 0 ? count / verticalTotal : 0;
                          const dashLen = pct * donutC;
                          const gapLen = donutC - dashLen;
                          const currentOffset = donutOffset;
                          donutOffset += dashLen;
                          return (
                            <circle key={name} cx="110" cy="110" r={donutR} fill="none"
                              stroke={VERTICAL_COLORS[i % VERTICAL_COLORS.length]}
                              strokeWidth={donutStroke}
                              strokeDasharray={`${dashLen} ${gapLen}`}
                              strokeDashoffset={-currentOffset}
                              strokeLinecap="round"
                              transform="rotate(-90 110 110)"
                            />
                          );
                        })}
                        <text x="110" y="105" textAnchor="middle" className="text-4xl font-bold" style={{ fill: '#1c1917' }}>{verticalTotal}</text>
                        <text x="110" y="128" textAnchor="middle" className="text-sm" style={{ fill: '#78716c' }}>total</text>
                      </svg>
                    </div>
                    <div className="flex-1 grid grid-cols-1 sm:grid-cols-2 gap-5">
                      {verticalEntries.map(([name, count], i) => {
                        const pct = verticalTotal > 0 ? Math.round((count / verticalTotal) * 100) : 0;
                        return (
                          <div key={name} className="flex items-center gap-3 p-3 rounded-lg bg-stone-50 border border-stone-100">
                            <span className="w-4 h-4 rounded-full shrink-0" style={{ backgroundColor: VERTICAL_COLORS[i % VERTICAL_COLORS.length] }} />
                            <span className="text-sm text-stone-700 flex-1">{name}</span>
                            <span className="text-base font-bold text-stone-900">{pct}%</span>
                            <span className="text-sm text-stone-400">{count}</span>
                          </div>
                        );
                      })}
                    </div>
                  </div>
                </CardContent>
              </Card>
            )}

            {/* Pipeline de Etapas - Horizontal */}
            <Card className="glass-card border-none" data-testid="pipeline-card">
              <CardContent className="p-5 sm:p-10">
              <p className="text-xs font-semibold uppercase tracking-widest text-stone-400 mb-4 sm:mb-10">
                  {language === 'es' ? 'PIPELINE DE ETAPAS' : 'STAGE PIPELINE'}
                </p>
                <div className="overflow-x-auto -mx-4 px-4 sm:mx-0 sm:px-0">
                  <div className="relative flex items-start justify-between min-w-[500px] px-4">
                  {/* Connecting line */}
                  <div className="absolute top-8 sm:top-9 left-12 right-12 h-1 bg-stone-200 z-0" />

                  {pipelineStatuses.map((status, i) => {
                    const count = stats.statusCounts[status] || 0;
                    const label = STATUS_MAP[status]?.[language] || status;
                    const colors = STAGE_COLORS[i % STAGE_COLORS.length];
                    const isActive = count > 0;
                    const size = 'w-14 h-14 sm:w-[72px] sm:h-[72px]';

                    return (
                      <div key={status} className="flex flex-col items-center z-10 flex-1" data-testid={`pipeline-stage-${status}`}>
                        {/* Count above circle */}
                        <p className={`text-xl sm:text-3xl font-black mb-2 ${isActive ? colors.text : 'text-stone-300'}`}>
                          {count}
                        </p>
                        {/* Circle */}
                        <div className={`${size} rounded-full border-4 flex items-center justify-center bg-white transition-all ${
                          isActive ? `${colors.border} shadow-lg ring-4 ring-offset-2 ${colors.bg.replace('bg-', 'ring-')}/30` : 'border-stone-200'
                        }`}>
                          <div className={`w-5 h-5 rounded-full ${isActive ? colors.bar : 'bg-stone-200'}`} />
                        </div>
                        {/* Label below */}
                        <p className={`text-xs font-semibold mt-3 text-center ${isActive ? 'text-stone-700' : 'text-stone-400'}`}>
                          {label}
                        </p>
                      </div>
                    );
                  })}
                </div>
                </div>
              </CardContent>
            </Card>

            {/* Applications Table */}
            <Card className="glass-card border-none" data-testid="applications-table-card">
              <CardContent className="p-5 sm:p-10">
                <div className="flex items-center justify-between mb-6">
                  <p className="text-xs font-semibold uppercase tracking-widest text-stone-400">
                    {language === 'es' ? 'POSTULACIONES' : 'APPLICATIONS'}
                  </p>
                  {applications.length > 0 && (
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={handleExportExcel}
                      disabled={exporting}
                      className="border-emerald-300 text-emerald-700 hover:bg-emerald-50"
                      data-testid="export-excel-btn"
                    >
                      {exporting ? <Loader2 className="w-4 h-4 mr-1 animate-spin" /> : <Download className="w-4 h-4 mr-1" />}
                      {language === 'es' ? 'Descargar Excel' : 'Export Excel'}
                    </Button>
                  )}
                </div>
                {applications.length === 0 ? (
                  <div className="text-center py-16 text-stone-400">
                    <Users className="w-14 h-14 mx-auto mb-4" />
                    <p className="text-lg">{language === 'es' ? 'Aún no hay postulaciones' : 'No applications yet'}</p>
                  </div>
                ) : (
                  <div className="overflow-x-auto">
                    <table className="w-full text-left">
                      <thead>
                        <tr className="border-b-2 border-stone-200 text-xs uppercase tracking-wider text-stone-500">
                          <th className="py-3 pr-4">{language === 'es' ? 'Startup' : 'Startup'}</th>
                          <th className="py-3 pr-4 hidden sm:table-cell">{language === 'es' ? 'Postulante' : 'Applicant'}</th>
                          <th className="py-3 pr-4">{language === 'es' ? 'Estado' : 'Status'}</th>
                          <th className="py-3 pr-4 hidden sm:table-cell">{language === 'es' ? 'Puntaje' : 'Score'}</th>
                          <th className="py-3 pr-4 hidden md:table-cell">{language === 'es' ? 'Fecha' : 'Date'}</th>
                          <th className="py-3 w-10"></th>
                        </tr>
                      </thead>
                      <tbody>
                        {applications.map((app, i) => {
                          const isMasked = app._masked === true;
                          const statusLabel = STATUS_MAP[app.status]?.[language] || app.status;
                          const statusColor = app.status === 'accepted' ? 'bg-emerald-100 text-emerald-700'
                            : app.status === 'rejected' ? 'bg-rose-100 text-rose-700'
                            : app.status === 'finalist' ? 'bg-blue-100 text-blue-700'
                            : app.status === 'under_review' ? 'bg-amber-100 text-amber-700'
                            : 'bg-stone-100 text-stone-700';
                          const avgScore = getAvgScore(app._id);
                          return (
                            <tr key={app._id || i} className={`border-b border-stone-100 transition-colors ${isMasked ? 'opacity-60' : 'hover:bg-stone-50'}`} data-testid={`app-row-${i}`}>
                              <td className="py-4 pr-4 font-semibold text-stone-900 text-sm sm:text-base">
                                {isMasked ? (
                                  <span className="flex items-center gap-2">
                                    <Lock className="w-3.5 h-3.5 text-gray-400" />
                                    <span className="blur-sm select-none">{app.startupName || 'Startup'}</span>
                                  </span>
                                ) : (app.startupName || '—')}
                              </td>
                              <td className="py-4 pr-4 text-stone-600 hidden sm:table-cell">
                                {isMasked ? <span className="blur-sm select-none">{'Nombre Oculto'}</span> : (app.founderName || app.userEmail || '—')}
                              </td>
                              <td className="py-4 pr-4">
                                <Badge className={`text-xs ${statusColor}`}>{statusLabel}</Badge>
                              </td>
                              <td className="py-4 pr-4 hidden sm:table-cell">
                                {avgScore ? (
                                  <div className="flex items-center gap-1.5">
                                    <Star className="w-4 h-4 text-amber-500 fill-amber-400" />
                                    <span className="font-bold text-stone-800">{avgScore}</span>
                                  </div>
                                ) : (
                                  <span className="text-stone-300 text-sm">—</span>
                                )}
                              </td>
                              <td className="py-4 pr-4 text-sm text-stone-500 hidden md:table-cell">
                                {app.submittedDate ? new Date(app.submittedDate).toLocaleDateString(language === 'es' ? 'es-CL' : 'en-US') : '—'}
                              </td>
                              <td className="py-4">
                                {isMasked ? (
                                  <Button variant="ghost" size="sm" className="text-purple-600 hover:text-purple-800 hover:bg-purple-50" data-testid={`app-unlock-btn-${i}`}
                                    onClick={() => { setPaywallTrigger('applications'); setPaywallOpen(true); }}
                                  >
                                    <Lock className="w-4 h-4 mr-1" />
                                    {language === 'es' ? 'Desbloquear' : 'Unlock'}
                                  </Button>
                                ) : (
                                  <Button variant="ghost" size="sm" className="text-indigo-600 hover:text-indigo-800 hover:bg-indigo-50" data-testid={`app-detail-btn-${i}`}
                                    onClick={() => navigate(`/organization/application/${app._id}`)}
                                  >
                                    <FileText className="w-4 h-4 mr-1" />
                                    {language === 'es' ? 'Ver' : 'View'}
                                  </Button>
                                )}
                              </td>
                            </tr>
                          );
                        })}
                      </tbody>
                    </table>
                  </div>
                )}
              </CardContent>
            </Card>
          </div>

          {/* Stats Sidebar */}
          <div className="hidden lg:flex flex-col gap-4 w-64 shrink-0">
            <Card className="glass-sidebar border-indigo-200/50 rounded-xl">
              <CardContent className="p-5">
                <p className="text-xs font-semibold uppercase tracking-wider text-indigo-500 mb-1">
                  {language === 'es' ? 'TOTAL POSTULACIONES' : 'TOTAL APPLICATIONS'}
                </p>
                <p className="text-3xl font-bold text-indigo-700">{stats.total}</p>
              </CardContent>
            </Card>

            <Card className="glass-sidebar border-emerald-200/50 rounded-xl">
              <CardContent className="p-5">
                <p className="text-xs font-semibold uppercase tracking-wider text-emerald-500 mb-1">
                  {language === 'es' ? 'ESTADO' : 'STATUS'}
                </p>
                <Badge className={`text-sm ${program.status === 'open' ? 'bg-emerald-200 text-emerald-800' : 'bg-stone-200 text-stone-700'}`}>
                  {program.status === 'open' ? (language === 'es' ? 'Abierto' : 'Open') : program.status === 'draft' ? (language === 'es' ? 'Borrador' : 'Draft') : (language === 'es' ? 'Cerrado' : 'Closed')}
                </Badge>
              </CardContent>
            </Card>

            <Card className="glass-sidebar border-amber-200/50 rounded-xl">
              <CardContent className="p-5">
                <p className="text-xs font-semibold uppercase tracking-wider text-amber-500 mb-1">
                  {language === 'es' ? 'DÍAS RESTANTES' : 'DAYS LEFT'}
                </p>
                <p className={`text-3xl font-bold ${stats.daysLeft <= 7 ? 'text-red-600' : stats.daysLeft <= 14 ? 'text-amber-600' : 'text-stone-800'}`}>
                  {stats.daysLeft ?? '—'}
                </p>
              </CardContent>
            </Card>

            <Card className="glass-sidebar border-blue-200/50 rounded-xl">
              <CardContent className="p-5">
                <p className="text-xs font-semibold uppercase tracking-wider text-blue-500 mb-1">
                  {language === 'es' ? 'EN REVISIÓN' : 'UNDER REVIEW'}
                </p>
                <p className="text-3xl font-bold text-blue-700">{stats.statusCounts['under_review'] || 0}</p>
              </CardContent>
            </Card>

            <Card className="glass-sidebar border-purple-200/50 rounded-xl">
              <CardContent className="p-5">
                <p className="text-xs font-semibold uppercase tracking-wider text-purple-500 mb-1">
                  {language === 'es' ? 'EVALUADOS' : 'SCORED'}
                </p>
                <p className="text-3xl font-bold text-purple-700">
                  {Object.keys(scores).length}
                </p>
              </CardContent>
            </Card>

            <Card className="glass-sidebar border-rose-200/50 rounded-xl">
              <CardContent className="p-5">
                <p className="text-xs font-semibold uppercase tracking-wider text-rose-500 mb-1">
                  {language === 'es' ? 'GANADORES' : 'WINNERS'}
                </p>
                <p className="text-3xl font-bold text-rose-700">{stats.statusCounts['accepted'] || 0}</p>
              </CardContent>
            </Card>

            {/* Feedback Toggle */}
            <Card className="glass-sidebar border-gray-200/50 rounded-xl" data-testid="feedback-toggle-card">
              <CardContent className="p-5">
                <div className="flex items-center justify-between">
                  <div className="flex items-center gap-2">
                    <MessageSquare className="w-4 h-4 text-gray-500" />
                    <p className="text-xs font-semibold uppercase tracking-wider text-gray-500">
                      {language === 'es' ? 'Feedback a postulantes' : 'Feedback to applicants'}
                    </p>
                  </div>
                  <button
                    onClick={async () => {
                      const newVal = !program?.showFeedbackToApplicants;
                      try {
                        await programsAPI.update(id, { showFeedbackToApplicants: newVal });
                        setProgram(prev => ({ ...prev, showFeedbackToApplicants: newVal }));
                        toast({ title: language === 'es'
                          ? (newVal ? 'Feedback visible para postulantes' : 'Feedback oculto para postulantes')
                          : (newVal ? 'Feedback visible to applicants' : 'Feedback hidden from applicants')
                        });
                      } catch (e) {
                        console.error(e);
                      }
                    }}
                    className={`w-11 h-6 rounded-full flex items-center px-0.5 transition-colors ${
                      program?.showFeedbackToApplicants ? 'bg-green-500' : 'bg-gray-300'
                    }`}
                    data-testid="feedback-toggle"
                  >
                    <div className={`w-5 h-5 rounded-full bg-white shadow transition-transform ${program?.showFeedbackToApplicants ? 'translate-x-5' : 'translate-x-0'}`} />
                  </button>
                </div>
                <p className="text-xs text-gray-400 mt-2">
                  {language === 'es'
                    ? (program?.showFeedbackToApplicants ? 'Los postulantes pueden ver los comentarios de evaluacion (sin puntajes)' : 'Los postulantes no pueden ver comentarios de evaluacion')
                    : (program?.showFeedbackToApplicants ? 'Applicants can see evaluation comments (no scores)' : 'Applicants cannot see evaluation comments')}
                </p>
              </CardContent>
            </Card>
          </div>
        </div>
      </div>
      <PaywallModal open={paywallOpen} onClose={() => setPaywallOpen(false)} trigger={paywallTrigger} />
    </div>
  );
}
